import React, { useState, useEffect, useRef } from 'react';

const OtpModal = ({ isOpen, onClose, onVerify, onResend, phoneNumber, apiError }) => {
  const length = 6; // Change to 4 if your backend uses 4-digit OTPs
  const [otp, setOtp] = useState(new Array(length).fill(""));
  const [timer, setTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const inputRefs = useRef([]);

  // Timer Logic
  useEffect(() => {
    let interval;
    if (isOpen && timer > 0) {
      interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
    } else if (timer === 0) {
      setCanResend(true);
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [isOpen, timer]);

  // 2. NEW: Reset State every time the modal opens
  useEffect(() => {
    if (isOpen) {
      setOtp(new Array(length).fill("")); // Wipe the OTP boxes
      setTimer(60);                       // Reset the countdown
      setCanResend(false);                // Hide the resend button
      
      // Auto-focus the first input box so the user can start typing instantly
      setTimeout(() => {
        if (inputRefs.current[0]) {
          inputRefs.current[0].focus();
        }
      }, 100);
    }
  }, [isOpen]); // This runs every time 'isOpen' changes

  // Handle individual input changes
  const handleChange = (element, index) => {
    const value = element.value.replace(/[^0-9]/g, ""); // Only numbers
    if (!value) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Focus next input
    if (index < length - 1 && value) {
      inputRefs.current[index + 1].focus();
    }
  };

  // Handle backspace navigation
  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const newOtp = [...otp];
      newOtp[index] = "";
      setOtp(newOtp);

      // Focus previous input if current is empty
      if (index > 0) {
        inputRefs.current[index - 1].focus();
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const otpValue = otp.join("");
    if (otpValue.length === length) {
      onVerify(otpValue);
    }
  };

  const handleResendClick = () => {
    setTimer(60);
    setCanResend(false);
    setOtp(new Array(length).fill("")); // Reset inputs
    inputRefs.current[0].focus(); // Focus first input
    onResend();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      {/* Container - Translated from .otp-Form */}
      <form 
        onSubmit={handleSubmit}
        className="relative flex flex-col items-center justify-center p-8 gap-5 bg-white rounded-[15px] shadow-[0px_0px_20px_rgba(0,0,0,0.08)] w-full max-w-sm sm:max-w-md animate-in zoom-in-95 duration-200"
      >
        {/* Exit Button - Translated from .exitBtn */}
        <button 
          type="button" 
          onClick={onClose}
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white shadow-[0px_0px_20px_rgba(0,0,0,0.17)] border-none text-black text-xl font-bold flex items-center justify-center cursor-pointer hover:bg-gray-100 transition-colors"
        >
          ×
        </button>

        <div className="text-center space-y-1">
          <span className="text-xl text-gray-900 font-bold">Enter OTP</span>
          <p className="text-sm text-gray-700 leading-tight px-4">
            We have sent a verification code to <br/>
            <span className="font-semibold text-indigo-600">{phoneNumber}</span>
          </p>
        </div>

        {/* Backend Error Display */}
        {apiError && (
          <p className="text-red-500 text-xs font-semibold bg-red-50 px-3 py-1.5 rounded-md w-full text-center">
            {apiError}
          </p>
        )}

        {/* Input Container - Translated from .inputContainer */}
        <div className="flex flex-row gap-2 sm:gap-3 items-center justify-center w-full my-2">
          {otp.map((data, index) => (
            <input
              key={index}
              type="text"
              name="otp"
              maxLength="1"
              value={data}
              onChange={(e) => handleChange(e.target, index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              ref={(ref) => (inputRefs.current[index] = ref)}
              className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-200 text-center border-none rounded-lg text-gray-800 outline-none font-semibold text-lg focus:bg-indigo-500/20 valid:bg-indigo-500/20 transition-all duration-300 shadow-inner"
            />
          ))}
        </div>

        {/* Verify Button - Translated from .verifyButton */}
        <button 
          type="submit"
          disabled={otp.join("").length < length}
          className="w-full h-12 border-none bg-[#7f81ff] hover:bg-[#9091ff] text-white font-semibold rounded-xl cursor-pointer transition-colors duration-200 disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          Verify
        </button>

        {/* Resend Note - Translated from .resendNote & .resendBtn */}
        <div className="flex flex-col items-center justify-center gap-1 mt-2 text-sm text-black">
          {timer > 0 ? (
            <p>Resend code in <span className="text-[#7f81ff] font-bold">{timer}s</span></p>
          ) : (
            <p>
              Didn't receive the code?{' '}
              <button 
                type="button" 
                onClick={handleResendClick}
                className="bg-transparent border-none text-[#7f81ff] hover:text-[#5e60e8] cursor-pointer text-base font-bold p-0 ml-1 transition-colors"
              >
                Resend Code
              </button>
            </p>
          )}
        </div>
      </form>
    </div>
  );
};

export default OtpModal;